import React, { useMemo, forwardRef } from 'react';
import { CircularProgress, FormControl, InputLabel, FormHelperText, Alert } from '@mui/material';
import ReactSelect from 'react-select';

const LoadingIndicator = () => <CircularProgress size={16} sx={{ mr: 1 }} />;
import useFieldCategories from '../_data_sources/useFieldCategories';

export type CategoryPickerProps = {
  id: string;
  label?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  size?: string;
  sx?: any;
  allowMultiple?: boolean;
};

const CategoryPicker = forwardRef<HTMLDivElement, CategoryPickerProps>(
  (
    {
      id,
      label,
      value = [],
      onChange,
      placeholder,
      disabled,
      required,
      error,
      helperText,
      size,
      sx,
      allowMultiple = false,
    },
    ref
  ) => {
    const { data: categories = [], isLoading: loading, error: apiError } = useFieldCategories();

    const options = useMemo(
      () =>
        (categories ?? []).map((cat) => ({
          value: String(cat.id),
          label: cat.name,
          category: cat,
        })),
      [categories]
    );

    // Single-select: value is the selected option object or null
    // Multi-select: value is an array of selected option objects
    const selectedValue = useMemo(() => {
      if (allowMultiple) {
        return options.filter((x) => (value ?? []).map(String).includes(String(x.value)));
      } else {
        return options.find((x) => String(x.value) === String(value?.[0])) || null;
      }
    }, [value, options, allowMultiple]);

    return (
      <FormControl
        fullWidth
        error={error}
        className={error ? 'Mui-error' : ''}
        sx={sx}
      >
        {!!label && <InputLabel required={required}>{label}</InputLabel>}
        {apiError ? (
          <Alert severity='error'>Failed to load categories</Alert>
        ) : (
          <ReactSelect
            name={id}
            value={selectedValue}
            options={options}
            placeholder={placeholder}
            isMulti={allowMultiple}
            isDisabled={disabled}
            isLoading={loading}
            components={{ LoadingIndicator }}
            classNamePrefix='react-select'
            menuPortalTarget={typeof window !== 'undefined' ? document.body : undefined}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
              input: (base) => ({
                ...base,
                boxShadow: 'none',
                outline: 0,
                minHeight: size === 'small' ? 26 : 45,
              }),
            }}
            onChange={(newValue) => {
              if (Array.isArray(newValue)) {
                onChange(newValue.map((x) => (typeof x === 'object' && 'value' in x ? String(x.value) : '')));
              } else if (newValue && typeof newValue === 'object' && 'value' in newValue) {
                onChange([String(newValue.value)]);
              } else {
                onChange([]);
              }
            }}
          />
        )}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default CategoryPicker;
